import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';

interface EditorFooterProps {
  readonly errorKind: 'forbidden' | 'generic' | null;
  readonly saving: boolean;
  readonly busy: boolean;
  /** Whether the working copy differs from the original — Save is disabled unless true. */
  readonly dirty: boolean;
  readonly onCancel: () => void;
  readonly onSave: () => void;
}

/** Error banner + Cancel/Save row rendered at the bottom of the care-team dialog. */
export function EditorFooter({
  errorKind,
  saving,
  busy,
  dirty,
  onCancel,
  onSave,
}: EditorFooterProps): ReactElement {
  const { t } = useTranslation();
  return (
    <>
      {errorKind !== null && (
        <div
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
        >
          <p className="font-semibold">{t('patient.careTeam.errorHeading')}</p>
          <p className="mt-0.5">
            {errorKind === 'forbidden'
              ? t('patient.careTeam.errorForbidden')
              : t('patient.careTeam.errorMessage')}
          </p>
        </div>
      )}

      <div className="flex items-center justify-end gap-2 pt-2">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={busy}>
          {t('patient.careTeam.cancel')}
        </Button>
        <Button type="button" onClick={onSave} disabled={busy || !dirty}>
          {saving ? t('patient.careTeam.saving') : t('patient.careTeam.save')}
        </Button>
      </div>
    </>
  );
}
